/**
 * Mode démo (front-only) — « Découvrir la plateforme ».
 *
 * Un drapeau de session qui, lorsqu'il est actif :
 *  - injecte des données fictives sur certains GET (voir `fixtures.ts`) ;
 *  - neutralise toutes les écritures (lecture seule) ;
 *  - affiche une bannière + lève les gates d'onboarding (contrat/mandat/démo).
 *
 * Aucune écriture en base, aucun risque pour le tenant réel : c'est une
 * surcouche purement front, réversible à tout instant (« Quitter la démo »).
 */
import { useSyncExternalStore } from "react";

const KEY = "victor.demo.active";
const listeners = new Set<() => void>();

export function isDemoActive(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function startDemo(): void {
  try {
    localStorage.setItem(KEY, "1");
  } catch {
    /* stockage indisponible */
  }
  emit();
}

export function stopDemo(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* stockage indisponible */
  }
  emit();
}

/**
 * Démarre le mode démo depuis l'UI : pose le drapeau, marque qu'on vient
 * de l'activer (pour relancer la visite), puis recharge sur l'accueil afin
 * de repartir sur un cache de requêtes propre (fixtures fraîches).
 */
export function launchDemo(): void {
  try {
    sessionStorage.setItem("victor.demo.justStarted", "1");
  } catch {
    /* stockage indisponible */
  }
  startDemo();
  window.location.assign("/");
}

function emit(): void {
  // Notifie les abonnés du même onglet (storage event ne se déclenche pas pour soi).
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // Synchronise aussi entre onglets.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) cb();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

/** Hook React : re-render quand le mode démo change. */
export function useDemoMode(): boolean {
  return useSyncExternalStore(subscribe, isDemoActive, () => false);
}

/** Évènement custom levé quand une écriture est bloquée en mode démo. */
export const DEMO_READONLY_EVENT = "victor:demo-readonly";
